import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Where a canvas item sits for Godot's light cull test: its accumulated `z_final` and the layer of
 * its canvas. Neither is a property of the node alone, so both come down the tree as contexts,
 * which only the light pass reads.
 */
/*
 * Portions ported from Godot Engine (MIT).
 * Copyright (c) 2014-present Godot Engine contributors.
 * Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 */
import { createContext, useContext } from 'react';
import { CANVAS_ITEM_Z_MIN, CANVAS_ITEM_Z_MAX } from '../../godot/rendering/rendering.js';
import { clamp } from '../../godot/math/math.js';
/** The world canvas's layer, which is where everything outside a CanvasLayer draws. */
export const WORLD_CANVAS_LAYER = 0;
/**
 * `_cull_canvas_item` (`servers/rendering/renderer_canvas_cull.cpp` lines 816-820 on master) clamps
 * only a `z_relative` sum, and `_attach_canvas_item_for_draw` (line 564) stores it as `z_final`. The
 * only z accumulation here: the y-sort pass (`_collect_ysort_children`, lines 160-166), the light
 * cull and the draw-order key (`canvasPaintOrder.ts`) all read it.
 */
export function accumulateCanvasItemZ(parentZ, props) {
    if (props.z_as_relative === false)
        return props.z_index;
    return clampCanvasItemZ(parentZ + props.z_index);
}
/** A z held inside the canvas z window, as `_cull_canvas_item` clamps a relative sum. */
export function clampCanvasItemZ(z) {
    return clamp(z, CANVAS_ITEM_Z_MIN, CANVAS_ITEM_Z_MAX);
}
const EffectiveZContext = createContext(0);
EffectiveZContext.displayName = 'EffectiveZContext';
/**
 * The accumulated `z_final` of the enclosing canvas item. 0 outside a 2D stage,
 * which is both the world canvas's starting value and what every 3D consumer
 * and bare test mount reads.
 */
export function useEffectiveZ() {
    return useContext(EffectiveZContext);
}
export function EffectiveZProvider({ value, children }) {
    return _jsx(EffectiveZContext.Provider, { value: value, children: children });
}
const CanvasLayerIndexContext = createContext(WORLD_CANVAS_LAYER);
CanvasLayerIndexContext.displayName = 'CanvasLayerIndexContext';
/** The `layer` of the canvas the enclosing item draws on. */
export function useCanvasLayerIndex() {
    return useContext(CanvasLayerIndexContext);
}
export function CanvasLayerIndexProvider({ value, children }) {
    return _jsx(CanvasLayerIndexContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=canvasItemPlacement.js.map